import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Share2, ArrowRight, ShieldCheck, ArrowLeft, CheckCircle2,
  Copy, UploadCloud, FileCheck, Users, ArrowLeftRight, ExternalLink
} from 'lucide-react';
import Footer from '@/components/Footer';

const DeudasSpeiInfo = () => {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Volver
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="sm" className="bg-stone-900 hover:bg-stone-800 text-white font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl active:scale-95 transition-all duration-150 ease-spring cursor-pointer shadow-sm">
                  Ver Planes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-stone-200/70 border border-stone-300 text-stone-800 px-3.5 py-1.5 rounded-full mb-6 text-xs sm:text-sm font-semibold shadow-xs">
              <Share2 className="w-4 h-4 text-indigo-600" />
              <span>Finanzas Compartidas Inteligentes</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-stone-900 mb-6 leading-tight tracking-tight">
              Control de deudas y cobros con{' '}
              <span className="text-indigo-600">liga para pago SPEI</span>
            </h1>

            <p className="text-base sm:text-lg text-stone-600 mb-8 leading-relaxed max-w-2xl mx-auto font-medium">
              Maneja deudas con alguien más o lo que tienen pendiente por pagarte. Comparte una liga pública con tu CLABE para que te transfieran directamente desde su banco y suban su comprobante de pago para conciliar en segundos.
            </p>

            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm sm:text-base px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Probar Gestor de Deudas
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-300 bg-white hover:bg-stone-100 text-stone-800 font-bold text-sm sm:text-base px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes y Precios
                </Button>
              </Link>
            </div>
          </div>

          {/* Demonstration Card */}
          <div className="max-w-4xl mx-auto mb-20">
            <Card className="p-6 sm:p-10 bg-white border border-stone-200 shadow-xl rounded-3xl">
              <div className="text-center mb-8">
                <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full">
                  Flujo Simple en 3 Pasos
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-stone-900 mt-3 tracking-tight">
                  Cómo funciona la Liga de Pago SPEI
                </h2>
              </div>

              <div className="grid md:grid-cols-3 gap-6 items-stretch">
                {/* Paso 1 */}
                <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 flex flex-col justify-between">
                  <div>
                    <div className="w-9 h-9 rounded-xl bg-stone-900 text-white flex items-center justify-center font-black text-sm mb-3">
                      1
                    </div>
                    <h3 className="font-bold text-stone-900 text-sm mb-1.5">Creas la Deuda y Compartes</h3>
                    <p className="text-xs text-stone-500 leading-relaxed font-medium">
                      Registras quién te debe y el monto. Generas un enlace público único y se lo envías por WhatsApp o mensaje.
                    </p>
                  </div>
                  <div className="mt-4 p-2.5 bg-white rounded-xl border border-stone-200 text-xs text-stone-500 flex items-center justify-between font-medium">
                    <span className="truncate">pgastos.com/pago/...</span>
                    <Share2 className="w-4 h-4 text-indigo-600 shrink-0 ml-1" />
                  </div>
                </div>

                {/* Paso 2 */}
                <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 flex flex-col justify-between">
                  <div>
                    <div className="w-9 h-9 rounded-xl bg-stone-900 text-white flex items-center justify-center font-black text-sm mb-3">
                      2
                    </div>
                    <h3 className="font-bold text-stone-900 text-sm mb-1.5">Tu Amigo Copia tu CLABE</h3>
                    <p className="text-xs text-stone-500 leading-relaxed font-medium">
                      La otra persona ve el saldo pendiente y puede copiar tu CLABE interbancaria en un toque para transferirte desde su app.
                    </p>
                  </div>
                  <div className="mt-4 p-2.5 bg-indigo-50/80 rounded-xl border border-indigo-200 text-xs font-mono text-indigo-900 flex items-center justify-between font-bold">
                    <span>CLABE: 0121800...</span>
                    <Copy className="w-4 h-4 text-indigo-600 shrink-0 ml-1" />
                  </div>
                </div>

                {/* Paso 3 */}
                <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 flex flex-col justify-between">
                  <div>
                    <div className="w-9 h-9 rounded-xl bg-stone-900 text-white flex items-center justify-center font-black text-sm mb-3">
                      3
                    </div>
                    <h3 className="font-bold text-stone-900 text-sm mb-1.5">Sube Comprobante y Concilia</h3>
                    <p className="text-xs text-stone-500 leading-relaxed font-medium">
                      En la misma liga adjunta la captura de su transferencia. Tú revisas el comprobante y marcas la deuda saldada.
                    </p>
                  </div>
                  <div className="mt-4 p-2.5 bg-emerald-50 rounded-xl border border-emerald-200 text-xs text-emerald-800 flex items-center justify-between font-bold">
                    <span>Comprobante Adjunto ✓</span>
                    <FileCheck className="w-4 h-4 text-emerald-600 shrink-0 ml-1" />
                  </div>
                </div>
              </div>

              {/* Nota de Transparencia */}
              <div className="mt-8 p-4 bg-stone-100 border border-stone-200 rounded-2xl text-xs text-stone-600 leading-relaxed flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-stone-900">Aclaración y Seguridad:</p>
                  <p>
                    PGastos no es un procesador de pagos ni retiene fondos de los usuarios. Las transferencias se realizan directamente de banco a banco mediante el sistema SPEI oficial de las instituciones bancarias mexicanas. PGastos actúa como organizador digital y gestor de comprobantes para evitar disputas y confusiones.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Pillars */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-20">
            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ArrowLeftRight className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Ambas Direcciones</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Controla deudas que tú tienes con otros (por pagar) y deudas que terceros tienen contigo (por cobrar).
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ExternalLink className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Liga Pública Segura</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Tu contacto no necesita registrarse ni descargar la aplicación para ver cuánto te debe, ver tu CLABE y subir su comprobante.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <UploadCloud className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Historial de Comprobantes</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Guarda fotos y PDFs de tickets y recibos bancarios de cada abono para que siempre haya evidencia transparente de las transacciones.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Users className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Gastos Compartidos</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Ideal para dividir cuentas de cenas, viajes en grupo o gastos de departamento entre roomies de forma organizada.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Saldos Parciales</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Registra abonos paulatinos. La plataforma recalcula el saldo pendiente automáticamente hasta liquidar la deuda por completo.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Sin Comisiones Ocultas</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Recibe el 100% de tu dinero directamente en tu banco. Sin comisiones por transacción ni retenciones de pasarelas.
              </p>
            </Card>
          </div>

          {/* CTA Card */}
          <Card className="p-8 sm:p-14 bg-stone-900 text-white text-center rounded-3xl border border-stone-800 shadow-xl">
            <h2 className="text-2xl sm:text-4xl font-black mb-3 tracking-tight">
              Cuentas claras, amistades largas
            </h2>
            <p className="text-sm sm:text-base text-stone-400 mb-8 max-w-xl mx-auto leading-relaxed font-medium">
              Evita el estrés de cobrar o recordar lo que debes. Administra todo con ligas claras y comprobantes respaldados.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Crear Cuenta Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-700 bg-stone-800 hover:bg-stone-700 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes Disponibles
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DeudasSpeiInfo;
